import '@components/common/layout/scrollbar.scss';

import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcDrone } from '@assets/icons/32x32';
import { Center, For, HStack, Icon, Spinner, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import NotificationCard from '@components/cockpit/panels/notification/card/NotificationCard';
import { DraggablePanel } from '@components/ui/draggable';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { useGetNotificationsQuery } from '@services/data/notification.api';
import { formatDate } from '@utils/date.utils';
import { extendedSensorShortHand } from '@utils/map/map.constants';
import { addYears } from 'date-fns';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { NotificationTypeEnum } from '@/types/data/data.types';
import { DateTimeEnum } from '@/types/dateTime.types';
import { PopupComponentProps, PopupTypeEnum } from '@/types/popup.types';

import TargetId from './infos/targets/TargetId';

type Props = PopupComponentProps<PopupTypeEnum.HISTORY>;

function HistoryPopup({ mapConstraintsRef, top, left, data, onClose }: Readonly<Props>) {
  const { identifiers, displayId, sensorType, sensorName, type, openingDate } = data;
  const { formatMessage } = useIntl();
  const bodyMaxHeight = useBreakpointValue({ base: 570, '4k': 664 });
  const activeSite = useAppSelector(selectActiveSite);

  const { data: notifications, isLoading } = useGetNotificationsQuery(
    {
      from: addYears(openingDate, -1).toISOString(),
      to: openingDate.toISOString(),
      site: activeSite!.code,
      displayIds: displayId ? [displayId] : [],
      identifiers: displayId ? [] : identifiers,
      types: [type],
      onlyEnded: true,
    },
    { skip: !activeSite },
  );

  const isDroneNotification = type === NotificationTypeEnum.DRONE;

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage
          id="historyPopup.title"
          values={{
            type: <FormattedMessage id={`notification.type.${type}`} />,
          }}
        />
      </DraggablePanel.Header>
      <DraggablePanel.Body maxHeight={bodyMaxHeight} width={{ base: '876px', '4k': '948px' }} minHeight={348}>
        <VStack gap={0.25} backgroundColor="neutral.900" flex={1} minHeight={0}>
          <HStack
            justifyContent="space-between"
            backgroundColor="neutral.700"
            padding="standardM"
            width="100%"
            flexShrink={0}
          >
            <HStack maxWidth="50%" gap={2}>
              {isDroneNotification ? (
                <>
                  <Icon as={IcDrone} boxSize={{ base: '24px', '4k': '32px' }} color="neutral.white" />
                  <TargetId
                    textStyle="screenStd"
                    label={
                      displayId ?? (identifiers.length > 0 ? identifiers[0] : formatMessage({ id: 'target.unknown' }))
                    }
                    isTechnical={displayId === null}
                  />
                </>
              ) : (
                <>
                  <Text fontSize="screenLg" fontWeight="medium" textTransform="uppercase">
                    {extendedSensorShortHand[sensorType]}
                  </Text>
                  <Text
                    textStyle="screenStd"
                    fontWeight="medium"
                    backgroundColor="neutral.black"
                    paddingX={0.5}
                    paddingY={0.25}
                  >
                    {sensorName}
                  </Text>
                </>
              )}
            </HStack>
            <Text textStyle="screenStd">
              <FormattedMessage
                id={isDroneNotification ? 'historyPopup.detection' : 'historyPopup.incident'}
                values={{ count: notifications?.length ?? 0 }}
              />
            </Text>
            <Text textStyle="screenStd">
              <FormattedMessage
                id="historyPopup.since"
                values={{ date: formatDate(addYears(openingDate, -1), DateTimeEnum.DATE) }}
              />
            </Text>
          </HStack>
          {isLoading ? (
            <Center width="100%" flex={1} padding="standardM">
              <Spinner boxSize="64px" color="neutral.300" />
            </Center>
          ) : (
            <VStack width="100%" flex={1} gap={0.25} overflow="hidden" overflowY="auto" className="map-panel-scrollbar">
              <For
                each={notifications}
                fallback={
                  <VStack gap={3} marginY="auto">
                    <EmptyPanelDroneIcon />
                    <Text
                      textStyle="screenStd"
                      fontWeight="medium"
                      color="neutral.200"
                      maxWidth="230px"
                      textAlign="center"
                      userSelect="none"
                    >
                      <FormattedMessage id="historyPopup.empty" />
                    </Text>
                  </VStack>
                }
              >
                {(notification) => (
                  <NotificationCard
                    key={notification.id}
                    isArchived
                    isHistory
                    archivedNotification={notification}
                    isSelectable={false}
                  />
                )}
              </For>
            </VStack>
          )}
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}

function arePropsEqual(oldProps: Readonly<Props>, newProps: Readonly<Props>) {
  return (
    shallowEqual(oldProps.data.displayId, newProps.data.displayId) &&
    shallowEqual(oldProps.data.identifiers, newProps.data.identifiers)
  );
}

export default memo(HistoryPopup, arePropsEqual);
